import { useState, useEffect, useCallback } from 'react';
import { voiceAgentHttp } from '@/lib/voice-agent-http';

/**
 * Fetches AI coordinator sessions for the given applications.
 * Returns a flat list of sessions sorted by created_at descending.
 * @param {Object} options
 * @param {Array} options.applications - ATS applications with id, ats_job, contact
 * @param {string} options.candidateName - Candidate name to attach to sessions
 * @param {string} options.candidateAvatarUrl - Fallback avatar URL when contact.avatar is not available
 * @returns {{ sessions: Array, isLoading: boolean, isError: boolean, refetch: Function }}
 */
export const useAICoordinatorSessions = ({
  applications = [],
  candidateName,
  candidateAvatarUrl,
}) => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchSessions = useCallback(
    async (showLoading = true) => {
      const applicationIds = applications?.map((app) => app.id) || [];
      if (applicationIds.length === 0) {
        setSessions([]);
        setIsLoading(false);
        return;
      }

      if (showLoading) {
        setIsLoading(true);
      }
      setIsError(false);

      try {
        const results = await Promise.all(
          applicationIds.map(async (appId) => {
            try {
              const response = await voiceAgentHttp({
                url: '/sessions',
                method: 'get',
                params: { application_id: appId },
              });
              const app = applications?.find((a) => a.id === appId);
              const jobTitle = app?.ats_job?.title || undefined;
              const name = candidateName || app?.contact?.name || undefined;
              const avatarUrl =
                app?.contact?.avatar ||
                app?.contact?.image_url ||
                candidateAvatarUrl;
              return (response?.sessions || []).map((session) => ({
                ...session,
                applicationId: appId,
                jobTitle,
                candidateName: name,
                candidateAvatarUrl: avatarUrl,
              }));
            } catch {
              return [];
            }
          })
        );

        const allSessions = results
          .flat()
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          );

        setSessions(allSessions);
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [applications, candidateName, candidateAvatarUrl]
  );

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  return { sessions, isLoading, isError, refetch: () => fetchSessions(false) };
};
